import React from "react";
import { Row, Col } from "reactstrap";
import { renderRichText } from "src/services/PrismicService";
import { VariationOverride } from "src/services/VariationService";
import GLOBAL_DATA from "../../../global-data.json";

import styles from "./styles.scss";
import { responsiveImage } from "src/lib/responsive-image";
const Logo = responsiveImage("/BetQL_Logo_360.png", [120, 240, 360]);
const AppStore = responsiveImage("/app-store.png", [128, 256, 314]);
const GooglePlay = responsiveImage("/google-play.png", [128, 256, 335]);

export interface FooterLink {
  text: string;
  link: string;
}

export interface FooterSectionConfig {
  title: string;
  footerLinkList: FooterLink[];
  xs: number;
  sm: number;
}

export interface FooterConfig {
  iosAppLink: string;
  androidAppLink: string;
  footerSectionConfigList: FooterSectionConfig[];
}

export interface WrapperFooterProps {
  footerConfig: FooterConfig;
  bottomOnly?: boolean;
}

/**
 * Build out the footer section according to the configuration object passed in. The job of this method
 * is to create the footer section with links/text with right container size for boostrap arrangement.
 *
 * @param footerSectionConfig Configuration
 */
const _buildFooterSection = (footerSectionConfig: FooterSectionConfig, key: number) => {
  return (
    <Col key={key} xs={6} sm={4} md={2} className="wrapper-footer__container">
      <div>
        <span className="wrapper-footer__title-text">{footerSectionConfig.title}</span>
        {footerSectionConfig.footerLinkList.map((link: any, index: number) => (
          <a
            key={index}
            href={link.link}
            className="wrapper-footer__link"
            target={link.link?.startsWith("/") ? "_self" : "_blank"}
            rel={link.link?.startsWith("/") ? undefined : "noopener nofollow"}
          >
            {link.text}
          </a>
        ))}
      </div>
    </Col>
  );
};

const Footer = (props: WrapperFooterProps) => {
  const { footerConfig, bottomOnly } = props;

  const { links, additional_informations } = GLOBAL_DATA.prismicAllFooters.edges[0].node;

  // revisitCkyConsent() only exists once the CookieYes banner script has loaded, so the
  // opt-out link is pointless without it configured.
  const cookieYesEnabled = !!process.env.NEXT_PUBLIC_COOKIEYES_CLIENT_ID;

  return (
    <footer className="wrapper-footer">
      {!bottomOnly && (
        <>
          <div className="wrapper-footer__app-links-container">
            <span className="wrapper-footer__title-text">DOWNLOAD APP</span>
            <div className="wrapper-footer__app-links">
              <a href={footerConfig.iosAppLink} className="wrapper-footer__app-link">
                <img
                  src={AppStore.src}
                  srcSet={AppStore.srcSet}
                  loading="lazy"
                  style={{ height: 48, maxWidth: 140 }}
                  alt="App Store Image"
                />
              </a>
              <a href={footerConfig.androidAppLink} className="wrapper-footer__app-link">
                <img
                  src={GooglePlay.src}
                  srcSet={GooglePlay.srcSet}
                  loading="lazy"
                  style={{ height: 48, maxWidth: 140 }}
                  alt="Google Play Store Image"
                />
              </a>
            </div>
          </div>
          <Row className="wrapper-footer__top">
            {footerConfig.footerSectionConfigList.map((footerSectionConfig: FooterSectionConfig, index: number) => {
              return _buildFooterSection(footerSectionConfig, index);
            })}
          </Row>
        </>
      )}
      <div className="wrapper-footer__bottom">
        <a href="#">
          <img
            src={Logo.src}
            srcSet={Logo.srcSet}
            loading="lazy"
            style={{ width: 120, marginBottom: 15 }}
            alt="Logo Image"
          />
        </a>

        <p className="wrapper-footer__text">© {new Date().getFullYear()} Skillbet Ltd., All Rights Reserved.</p>

        <p className="wrapper-footer__text">
          {links?.map(({ name, url }, i) => (
            <span key={name}>
              <a className="wrapper-footer__text-link" href={url}>
                {name}
              </a>
              {i < links.length - 1 && " | "}
            </span>
          ))}
        </p>

        {cookieYesEnabled && (
          <p className="wrapper-footer__text">
            <button type="button" className="wrapper-footer__text-link" onClick={() => window.revisitCkyConsent?.()}>
              Do Not Sell or Share My Personal Information
            </button>
          </p>
        )}

        {additional_informations?.map(({ text }, index) => (
          <div className="wrapper-footer__text" key={`footer-text-${index}`}>
            {renderRichText(text)}
          </div>
        ))}

        <VariationOverride className="wrapper-footer__text" style={{ marginBottom: 16 }} />
      </div>
      <style jsx>{styles}</style>
    </footer>
  );
};

Footer.defaultProps = {
  footerConfig: {
    iosAppLink: "https://itunes.apple.com/app/apple-store/id1334825645?pt=118678143&ct=BetQLWebapp&mt=8",
    androidAppLink:
      "https://play.google.com/store/apps/details?id=com.rotoql.betql&referrer=utm_source%3DBetQL%2520Webapp",
    footerSectionConfigList: [
      {
        title: "PRODUCT",
        footerLinkList: [
          {
            text: "RotoQL",
            link: "https://www.rotoql.com/product/",
          },
          {
            text: "RotoQL Mobile App",
            link: "https://www.rotoql.com/mobile/",
          },
          {
            text: "BetQL B2B",
            link: "/contact-us-b2b",
          },
          {
            text: "BetQL Affiliate Program",
            link: "/become-betql-affiliate",
          },
          {
            text: "Press",
            link: "/press",
          },
        ],
      },
      {
        title: "SPORTS BETTING",
        footerLinkList: [
          {
            text: "Lines and Odds",
            link: "/lines-odds-sitemap",
          },
          {
            text: "Public Betting Lines",
            link: "/public-betting-sitemap",
          },
          {
            text: "Sharp Picks",
            link: "/sharp-picks-sitemap",
          },
          {
            text: "Line Movement",
            link: "/line-movement-sitemap",
          },
          {
            text: "Betting News",
            link: "/news",
          },
          {
            text: "Sports Betting FAQ",
            link: "/sports-betting-faq",
          },
        ],
      },
      {
        title: "HELP",
        footerLinkList: [
          {
            text: "Getting Started",
            link: "https://support.betql.co/hc/en-us",
          },
          {
            text: "Customer Services",
            link: "https://support.betql.co/hc/en-us/requests/new",
          },
          {
            text: "FAQ",
            link: "https://support.betql.co/hc/en-us/categories/360003861073-FAQs",
          },
          {
            text: "Sportsbook Review",
            link: "/sportsbook-review",
          },
        ],
      },
      {
        title: "SOCIAL",
        footerLinkList: [
          {
            text: "Twitter",
            link: "https://twitter.com/betqlapp",
          },
          {
            text: "Facebook",
            link: "https://www.facebook.com/betql/",
          },
          {
            text: "LinkedIn",
            link: "https://www.linkedin.com/company/rotoql",
          },
          {
            text: "Instagram",
            link: "https://www.instagram.com/betqlapp/",
          },
        ],
      },
      {
        title: "POPULAR PAGES",
        footerLinkList: [
          {
            text: "NFL Picks",
            link: "/nfl/sharp-picks",
          },
          {
            text: "NFL Odds",
            link: "/nfl/odds",
          },
          {
            text: "College Football Picks",
            link: "/ncaaf/sharp-picks",
          },
          {
            text: "College Football Odds",
            link: "/ncaaf/odds",
          },
          {
            text: "College Basketball Picks",
            link: "/ncaab/sharp-picks",
          },
          {
            text: "College Basketball Odds",
            link: "/ncaab/odds",
          },
        ],
      },
      {
        title: "POPULAR PAGES",
        footerLinkList: [
          {
            text: "NBA Picks",
            link: "/nba/sharp-picks",
          },
          {
            text: "NBA Odds",
            link: "/nba/odds",
          },
          {
            text: "MLB Picks",
            link: "/mlb/sharp-picks",
          },
          {
            text: "MLB Odds",
            link: "/mlb/odds",
          },
          {
            text: "NHL Picks",
            link: "/nhl/sharp-picks",
          },
          {
            text: "NHL Odds",
            link: "/nhl/odds",
          },
        ],
      },
    ],
  },
};

export default Footer;
